import type { Me, MembershipSummary } from "@payezy/api-client/public";
import { getMeOptions } from "@payezy/api-client/public/query";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

export function useMe(): Me {
  return useSuspenseQuery(getMeOptions()).data;
}

export type OrgContext = MembershipSummary & { can: (perm: string) => boolean; readOnly: boolean };

/** Current organization (from the /o/$orgId route) and the caller's permissions in it. */
export function useOrg(): OrgContext {
  const me = useMe();
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const m = me.memberships.find((x) => x.org_id === orgId) ?? me.memberships[0];
  if (!m) throw new Error("no organization");
  const readOnly = me.session.impersonation?.scope === "read";
  return { ...m, readOnly, can: (perm) => !readOnly && m.permissions.includes(perm) };
}

export const orgStatusLabel: Record<string, { label: string; tone: "neutral" | "success" | "warning" | "danger" | "info" }> = {
  kyc_required: { label: "Verification required", tone: "warning" },
  kyc_pending: { label: "Verification in review", tone: "info" },
  active: { label: "Active", tone: "success" },
  restricted: { label: "Restricted", tone: "warning" },
  suspended: { label: "Suspended", tone: "danger" },
  closed: { label: "Closed", tone: "neutral" },
};
